import { CircularProgress } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import React, { useState, useRef, ChangeEvent, KeyboardEvent } from 'react';

interface OTPInputProps {
  onSubmit: (otp: string) => void;
  onResend: () => void;
  numberOfColunms?: number;
  isLoading?: boolean;
  isSubmitting?: boolean;
  isResending?: boolean;
}

const OTPInput: React.FC<OTPInputProps> = ({ onSubmit, onResend, numberOfColunms = 6, isLoading, isResending, isSubmitting }) => {
  const [otp, setOtp] = useState<string[]>(Array(numberOfColunms).fill(''));
  const inputRefs = useRef<HTMLInputElement[]>([]);

  const handleChange = (index: number, value: string) => {
    if (value.match(/^[0-9]{0,1}$/)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Focus the next input field
      if (value && index < otp.length - 1) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !otp[index]) {
      // Clear the current input and focus on the previous input
      if (index > 0) {
        const newOtp = [...otp];
        newOtp[index - 1] = '';
        setOtp(newOtp);
        inputRefs.current[index - 1].focus();
      }
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pasteData = event.clipboardData.getData('Text');
    if (pasteData.length === 6 && /^[0-9]+$/.test(pasteData)) {
      const newOtp = pasteData.split('');
      setOtp(newOtp);
      newOtp.forEach((_, index) => {
        if (inputRefs.current[index]) {
          inputRefs.current[index].focus();
        }
      });
    }
    event.preventDefault();
  };

  const handleSubmit = () => {
    onSubmit(otp.join(''));
  };

  const handleResend = () => {
    onResend();
    setOtp(Array(numberOfColunms).fill(''));
    inputRefs.current[0].focus();
  };

  const isOtpComplete = otp.join('').length === numberOfColunms;

  return (
    <div style={styles.container}>
      <div style={styles.otpContainer}>
        {otp.map((digit, index) => (
          <input
            key={index}
            type="text"
            inputMode="numeric"
            value={digit}
            maxLength={1}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(index, e.target.value)}
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            ref={(el) => (inputRefs.current[index] = el!)}
            style={{
              ...styles.otpInput,
              ...(isLoading || isSubmitting ? styles.disabledInput : {}),
              ...(digit ? styles.filledInput : {})
            }}
            disabled={isLoading || isSubmitting}
            autoFocus={index === 0 && !isLoading}
          />
        ))}
      </div>

      <div style={styles.timerContainer}>
        <AccessTimeIcon style={styles.timerIcon} />
        <span style={styles.timerText}>OTP expires in 10 minutes</span>
      </div>

      <div style={styles.buttonContainer}>
        <button
          type="button"
          onClick={handleResend}
          style={{
            ...styles.resendButton,
            ...(isResending ? styles.disabledButton : {})
          }}
          disabled={isResending || isLoading || isSubmitting}
        >
          {isResending ? (
            <>
              <CircularProgress size={16} style={styles.buttonSpinner} />
              Resending...
            </>
          ) : (
            'Resend OTP'
          )}
        </button>

        <button
          type="button"
          onClick={() => handleSubmit()}
          style={{
            ...styles.submitButton,
            ...(!isOtpComplete ? styles.disabledButton : {}),
            ...(isLoading || isSubmitting ? styles.loadingButton : {})
          }}
          disabled={!isOtpComplete || isLoading || isSubmitting}
        >
          {(isLoading || isSubmitting) ? (
            <>
              <CircularProgress size={16} style={styles.buttonSpinner} />
              Verifying...
            </>
          ) : (
            'Verify OTP'
          )}
        </button>
      </div>

      {(isLoading || isSubmitting) && (
        <div style={styles.overlay}>
          <CircularProgress style={styles.loadingSpinner} />
          <p style={styles.loadingText}>Verifying OTP...</p>
        </div>
      )}
    </div>
  );
};

// Styles for the component
const styles: { [key: string]: React.CSSProperties } = {
  modalContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(5px)',
  },
  modalContent: {
    padding: '40px 30px 30px',
    backgroundColor: '#fff',
    borderRadius: '20px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
    maxWidth: '480px',
    width: '90%',
    textAlign: 'center',
    position: 'relative',
    border: '1px solid #f0f0f0',
  },
  header: {
    marginBottom: '10px',
  },
  iconContainer: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    backgroundColor: '#e3f2fd',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
  },
  securityIcon: {
    fontSize: '40px',
    color: '#1976d2',
  },
  modalTitle: {
    marginBottom: '8px',
    fontSize: '1.5rem',
    fontWeight: '600',
    color: '#1a1a1a',
  },
  subtitle: {
    margin: '0 0 20px 0',
    fontSize: '0.95rem',
    color: '#666',
    fontWeight: '400',
  },
  divider: {
    margin: '20px 0 30px',
    backgroundColor: '#f0f0f0',
  },
  closeButtonIcon: {
    position: 'absolute',
    top: '15px',
    right: '15px',
    color: '#999',
    backgroundColor: '#f8f9fa',

  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  otpContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    marginBottom: '25px',
    flexWrap: 'wrap',
  },
  otpInput: {
    width: '52px',
    height: '60px',
    fontSize: '24px',
    fontWeight: '600',
    textAlign: 'center',
    borderRadius: '12px',
    border: '2px solid #e1e5e9',
    backgroundColor: '#fafbfc',
    transition: 'all 0.2s ease-in-out',
    outline: 'none',
    color: '#1a1a1a',

  },
  filledInput: {
    borderColor: '#4caf50',
    backgroundColor: '#f8fff8',
  },
  disabledInput: {
    opacity: 0.6,
    pointerEvents: 'none',
    backgroundColor: '#f5f5f5',
  },
  timerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '25px',
    padding: '12px 20px',
    backgroundColor: '#fff3e0',
    borderRadius: '10px',
    border: '1px solid #ffe0b2',
  },
  timerIcon: {
    fontSize: '18px',
    color: '#f57c00',
  },
  timerText: {
    fontSize: '0.9rem',
    color: '#e65100',
    fontWeight: '500',
  },
  buttonContainer: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
    width: '100%',
  },
  resendButton: {
    padding: '12px 24px',
    backgroundColor: '#6c757d',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '500',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: '120px',
    justifyContent: 'center',
  },
  submitButton: {
    padding: '12px 32px',
    backgroundColor: '#1976d2',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '500',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: '140px',
    justifyContent: 'center',

  },
  disabledButton: {
    opacity: 0.6,
    cursor: 'not-allowed',
    transform: 'none !important',
  },
  loadingButton: {
    backgroundColor: '#1976d2',
  },
  buttonSpinner: {
    color: 'inherit',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '20px',
    backdropFilter: 'blur(2px)',
  },
  loadingSpinner: {
    color: '#1976d2',
    marginBottom: '15px',
  },
  loadingText: {
    margin: 0,
    fontSize: '0.95rem',
    color: '#666',
    fontWeight: '500',
  },
};

export default OTPInput;
